import { Constructor } from '@angular/cdk/table';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IRelatedContent } from 'src/app/core/models/i-related-content';
import { compareArrays } from '../utils/array-utils';

type WithRelatedArticlesFormSectionType = {
  _initRelatedArticles(relatedArticles: IRelatedContent[]): void;
  _initRelatedArticlesValueChanges(
    getPreviousRelatedArticles: () => IRelatedContent[] | null,
  ): void;
  _buildRelatedArticles(): IRelatedContent[];
  get relatedArticles(): IRelatedContent[];
};

type RelatedArticlesFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: {
    RELATED_ARTICLES: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithRelatedArticlesFormSection<
  TBase extends Constructor<RelatedArticlesFormSectionRequirementsBase>,
>(
  baseClass: TBase,
): {
  new (...args: any[]): WithRelatedArticlesFormSectionType;
  prototype: WithRelatedArticlesFormSectionType;
} & TBase {
  return class RelatedArticlesSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initRelatedArticles(relatedArticles: IRelatedContent[]): void {
      const relatedArticlesControl = this.form.get(
        this.FORM_PATHS.RELATED_ARTICLES(),
      ) as FormArray<FormControl<IRelatedContent>>;

      relatedArticles.forEach((article) =>
        relatedArticlesControl.push(new FormControl(article)),
      );

      relatedArticlesControl.markAsPristine();
    }

    _initRelatedArticlesValueChanges(
      getPreviousRelatedArticles: () => IRelatedContent[] | null,
    ): void {
      this.form
        .get(this.FORM_PATHS.RELATED_ARTICLES())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousRelatedArticles())) {
              this.form
                .get(this.FORM_PATHS.RELATED_ARTICLES())
                .markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.RELATED_ARTICLES()).markAsDirty();
            }
          },
        });
    }

    _buildRelatedArticles(): IRelatedContent[] {
      return this.form.get(this.FORM_PATHS.RELATED_ARTICLES()).value;
    }

    get relatedArticles(): IRelatedContent[] {
      return this.form.get(this.FORM_PATHS.RELATED_ARTICLES()).value;
    }
  };
}
